import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Clock, Award, Calendar, TrendingDown, MinusSquare } from 'lucide-react';
import { BestGame, getBestGamesByWinrate } from '../lib/game/sessionService';
import { useTheme } from '../contexts/ThemeContext';

interface BestGamesListProps {
  userId: string;
  minPlays?: number;
  limit?: number;
}

const BestGamesList: React.FC<BestGamesListProps> = ({ 
  userId, 
  minPlays = 3,
  limit = 5
}) => {
  const { theme } = useTheme();
  const [bestGames, setBestGames] = useState<BestGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadBestGames = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const data = await getBestGamesByWinrate(userId, minPlays);
        setBestGames(data.slice(0, limit));
      } catch (err) {
        console.error('Erro ao carregar melhores jogos:', err);
        setError('Não foi possível carregar os melhores jogos.');
      } finally {
        setLoading(false);
      }
    };

    loadBestGames();
  }, [userId, minPlays, limit]);

  if (loading) {
    return (
      <div className={`p-6 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
        <div className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${theme === 'dark' ? 'border-indigo-400' : 'border-indigo-500'} mx-auto`}></div>
        <p className="mt-2">Carregando melhores jogos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-red-900/50 text-red-300' : 'bg-red-50 text-red-700'}`}>
        <p>{error}</p>
      </div>
    );
  }

  if (bestGames.length === 0) {
    return (
      <div className={`p-6 rounded-lg text-center ${theme === 'dark' ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
        <Trophy className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'} mb-2`}>
          Nenhum jogo com estatísticas suficientes
        </h3>
        <p className="mb-4">
          Jogue mais partidas (pelo menos {minPlays} por jogo) para ver seus melhores desempenhos aqui.
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow overflow-hidden ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
      <div className={`p-4 border-b ${theme === 'dark' ? 'bg-yellow-700/30 border-yellow-600/50' : 'bg-yellow-50 border-yellow-100'}`}>
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'} flex items-center`}>
          <Trophy className={`h-5 w-5 mr-2 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`} />
          Seus Melhores Jogos
        </h3>
        <p className={`text-sm ${theme === 'dark' ? 'text-yellow-400/80' : 'text-yellow-600'} mt-1`}>
          Jogos com maior taxa de vitória (mínimo de {minPlays} partidas)
        </p>
      </div>

      <div className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
        {bestGames.map((game, index) => (
          <div key={game.game_id} className={`p-4 ${index === 0 && theme === 'dark' ? 'bg-yellow-700/20' : index === 0 ? 'bg-yellow-50' : (theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50') }`}>
            <div className="flex items-center mb-2">
              {index === 0 && (
                <span className={`flex-shrink-0 mr-2 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'}`}>
                  <Trophy className="h-6 w-6" />
                </span>
              )}
              <Link 
                to={`/games/${game.slug}`}
                className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-100 hover:text-indigo-300' : 'text-gray-900 hover:text-indigo-600'}`}
              >
                {game.game_name}
              </Link>
              <span className={`ml-2 text-xl font-bold ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>{game.win_rate}%</span>
            </div>

            <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2`}>
              <div className="flex items-center">
                <Award className={`h-4 w-4 mr-1 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} />
                <span>{game.wins} de {game.play_count} vitórias</span>
              </div>
              {game.avg_duration && (
                <div className="flex items-center">
                  <Clock className={`h-4 w-4 mr-1 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} />
                  <span>~{Math.round(game.avg_duration)} min</span>
                </div>
              )}
              <div className="flex items-center">
                <Calendar className={`h-4 w-4 mr-1 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} />
                <span>
                  {new Date(game.last_played).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BestGamesList; 